/**
 * @file        src/api/seo.js
 * @description SEO Management API calls (/api/seo, admin migration 077). Every
 *              call names a BRAND: SEO is per storefront, and the backend never
 *              defaults one.
 *
 *              Previews are server-rendered on purpose: `previewSeo` returns the
 *              storefront resolver's own answer for a draft, so the Google and
 *              social cards on screen are what the storefront will actually
 *              serve — never a second derivation made from the form values.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */
import { get, put, post, patch, del } from './client';

/** Brand-level defaults. A brand with no row answers an all-default object (configured:false). */
export const getSeoSettings = (brand) => get('/seo/settings', { params: { brand } });
export const saveSeoSettings = (body) => put('/seo/settings', body);

/** Every indexable storefront page, configured or not. meta: { siteNoindexReason, settingsConfigured }. */
export const listSeoPages = (brand) => get('/seo/pages', { params: { brand } });
/** { entry, row, resolved, fallbacks, siteNoindexReason } */
export const getSeoPage = (brand, pageKey) => get(`/seo/pages/${encodeURIComponent(pageKey)}`, { params: { brand } });
export const saveSeoPage = (pageKey, body) => put(`/seo/pages/${encodeURIComponent(pageKey)}`, body);
export const resetSeoPage = (brand, pageKey) => del(`/seo/pages/${encodeURIComponent(pageKey)}`, { params: { brand } });

/**
 * body: { brand, path?, pageKey?, page?, settings?, item?, itemKind?:'blog'|'package' }
 * -> resolve() output (production view) + { path, siteNoindexReason }.
 * `silent` keeps the global load bar still while the editor types.
 */
export const previewSeo = (body) => post('/seo/preview', body, { silent: true });

/** params: { brand, q?, isActive?, page, pageSize } -> { data: rows (each with `problem`), meta } */
export const listRedirects = (params) => get('/seo/redirects', { params });
export const createRedirect = (body) => post('/seo/redirects', body);
export const updateRedirect = (id, body) => patch(`/seo/redirects/${id}`, body);
export const deleteRedirect = (id) => del(`/seo/redirects/${id}`);
/** All or nothing. A refusal carries error.fields.rows (per-row errors) or error.fields.problems (loops/chains). */
export const importRedirects = (body) => post('/seo/redirects/import', body);

/** { summary: { errors, warnings, byKind }, rows: [{severity, kind, message, target, link}] } */
export const getSeoAudit = (brand) => get('/seo/audit', { params: { brand } });
/** { served, generated, custom, blocked, canonicalHost } */
export const getRobotsPreview = (brand) => get('/seo/robots-preview', { params: { brand } });
/** { enabled, canonicalHost, siteNoindexReason, urlCount, pages, blogs, packages, sitemapUrl } */
export const getSitemapStatus = (brand) => get('/seo/sitemap-status', { params: { brand } });
